import React, { useState } from "react";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, List, ListItem, ListItemText, Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import RestoreIcon from "@mui/icons-material/Restore";
import {
  useGetPlaylistVersionsQuery, usePublishPlaylistMutation, useRollbackPlaylistMutation,
} from "../../../Api/attoDeskApi";
import { IPlaylistVersionEntry } from "../../../Api/Interface/api.interface";
import { TabCard } from "./menuTabsUi";
import { useNotifier } from "../../../Core/Notifier";
import { GOLD, NAVY, TEXT_SECONDARY } from "../../../theme/tokens";
import { MenuTabProps } from "./MenuTabs";

/** Publish restore points + roll back to a previous published version. */
const HistoryTab: React.FC<MenuTabProps> = ({ playlistId }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const { data, isLoading, refetch } = useGetPlaylistVersionsQuery(playlistId);
  const [publish, { isLoading: publishing }] = usePublishPlaylistMutation();
  const [rollback, { isLoading: rollingBack }] = useRollbackPlaylistMutation();
  const [confirm, setConfirm] = useState<IPlaylistVersionEntry | null>(null);

  const versions: IPlaylistVersionEntry[] = (data?.data as any[]) ?? [];

  const doPublish = async () => {
    try {
      await publish({ id: playlistId }).unwrap();
      showMessage("Published a new restore point.");
      refetch();
    } catch { showErrorMessage("Publish failed."); }
  };

  const doRollback = async () => {
    if (!confirm) return;
    try {
      await rollback({ id: playlistId, version: confirm.version }).unwrap();
      showMessage(`Rolled back to version ${confirm.version}.`);
      setConfirm(null);
      refetch();
    } catch { showErrorMessage("Rollback failed."); }
  };

  return (
    <>
      <TabCard title="Version History" subtitle="Publish restore points and roll back"
        icon={<HistoryIcon fontSize="small" />}
        right={
          <Button size="small" variant="contained" disabled={publishing}
            onClick={doPublish}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: NAVY, "&:hover": { bgcolor: "#08304a" } }}>
            {publishing ? "Publishing…" : "Publish restore point"}
          </Button>
        }>
        {isLoading ? (
          <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={26} /></Box>
        ) : versions.length === 0 ? (
          <Typography sx={{ color: TEXT_SECONDARY, textAlign: "center", py: 4 }}>
            No published versions yet. Publish a restore point to enable rollback.
          </Typography>
        ) : (
          <List disablePadding>
            {versions.map((v) => (
              <ListItem key={v.id} divider
                secondaryAction={
                  <Button size="small" startIcon={<RestoreIcon />}
                    onClick={() => setConfirm(v)}
                    sx={{ textTransform: "none", fontWeight: 700, color: NAVY }}>
                    Roll back
                  </Button>
                }>
                <ListItemText
                  primary={
                    <Typography sx={{ fontWeight: 700, color: NAVY }}>
                      Version {v.version}
                      {v.label ? <span style={{ color: GOLD }}> · {v.label}</span> : null}
                    </Typography>
                  }
                  secondary={
                    [v.creator?.name || v.creator?.username, v.created_at
                      ? new Date(v.created_at).toLocaleString() : null]
                      .filter(Boolean).join(" · ")
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </TabCard>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>Roll back to version {confirm?.version}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
            This restores the menu content from version {confirm?.version} as a new
            version. Devices will re-fetch on their next poll.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={rollingBack}
            onClick={doRollback} sx={{ textTransform: "none", fontWeight: 700 }}>
            {rollingBack ? "Rolling back…" : "Roll back"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HistoryTab;
